import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import AdminLayout from '../components/AdminLayout';
import { Card } from '../components/ui/card';
import { API_BASE_URL } from '../config/api';
import {
  Briefcase,
  Users,
  FileText,
  DollarSign,
  MessageSquare,
  TrendingUp,
  ArrowRight
} from 'lucide-react';

export default function AdminOverview() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    programs: 0,
    users: 0,
    papers: 0,
    funding: 0,
    inquiries: 0,
    memberships: 0
  });
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/login');
      return;
    }
    if (user.role !== 'admin') {
      navigate('/');
      return;
    }
    fetchDashboardData();
  }, [user, authLoading, navigate]);

  const fetchDashboardData = async () => {
    try {
      // Fetch statistics from various endpoints
      const [programsRes, papersRes, fundingRes, inquiriesRes, usersRes, membersRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/programs`).catch(() => ({ data: [] })),
        axios.get(`${API_BASE_URL}/api/papers/`).catch(() => ({ data: [] })),
        axios.get(`${API_BASE_URL}/api/funding-options`).catch(() => ({ data: [] })),
        axios.get(`${API_BASE_URL}/api/contact/inquiries`).catch(() => ({ data: { inquiries: [] } })),
        axios.get(`${API_BASE_URL}/api/auth/users`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API_BASE_URL}/api/membership-applications/admin/list`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` },
          withCredentials: true
        }).catch(() => ({ data: { total: 0 } }))
      ]);

      setStats({
        programs: programsRes.data?.length || 0,
        papers: papersRes.data?.length || 0,
        funding: fundingRes.data?.length || 0,
        inquiries: inquiriesRes.data?.inquiries?.length || 0,
        users: usersRes.data?.length || 0,
        memberships: membersRes.data?.total || 0
      });
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    {
      title: 'Programs',
      value: stats.programs,
      icon: Briefcase,
      color: 'bg-blue-500',
      link: '/admin/programs'
    },
    {
      title: 'Research Papers',
      value: stats.papers,
      icon: FileText,
      color: 'bg-green-500',
      link: '/admin/research'
    },
    {
      title: 'Funding Options',
      value: stats.funding,
      icon: DollarSign,
      color: 'bg-purple-500',
      link: '/admin/funding'
    },
    {
      title: 'Users',
      value: stats.users,
      icon: Users,
      color: 'bg-orange-500',
      link: '/admin/users'
    },
    {
      title: 'Contact Inquiries',
      value: stats.inquiries,
      icon: MessageSquare,
      color: 'bg-pink-500',
      link: '/admin/inquiries'
    },
    {
      title: 'Memberships',
      value: stats.memberships,
      icon: TrendingUp,
      color: 'bg-indigo-500',
      link: '/admin/members'
    }
  ];

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1E3A5F] mx-auto mb-4"></div>
            <p className="text-gray-500">Loading dashboard...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Dashboard Overview</h1>
        <p className="text-gray-600">Welcome back, {user?.name || 'Admin'}! Here's what's happening with CSED.</p>
      </div>

      {/* Statistics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        {statCards.map((stat) => {
          const Icon = stat.icon;
          return (
            <Card
              key={stat.title}
              className="p-6 hover:shadow-lg transition-shadow cursor-pointer"
              onClick={() => navigate(stat.link)}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 mb-1">{stat.title}</p>
                  <p className="text-3xl font-bold text-gray-900">{stat.value}</p>
                </div>
                <div className={`${stat.color} p-4 rounded-full`}>
                  <Icon className="text-white" size={24} />
                </div>
              </div>
              <div className="mt-4 flex items-center text-sm text-[#E87722] hover:underline">
                <span>View all</span>
                <ArrowRight size={16} className="ml-1" />
              </div>
            </Card>
          );
        })}
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Activity */}
        <Card className="p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Quick Actions</h2>
          <div className="space-y-3">
            <button
              onClick={() => navigate('/admin/programs')}
              className="w-full text-left px-4 py-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors flex items-center justify-between"
            >
              <span className="font-medium text-gray-700">Manage Programs</span>
              <ArrowRight size={18} className="text-gray-400" />
            </button>
            <button
              onClick={() => navigate('/admin/research')}
              className="w-full text-left px-4 py-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors flex items-center justify-between"
            >
              <span className="font-medium text-gray-700">Manage Research Papers</span>
              <ArrowRight size={18} className="text-gray-400" />
            </button>
            <button
              onClick={() => navigate('/admin/funding')}
              className="w-full text-left px-4 py-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors flex items-center justify-between"
            >
              <span className="font-medium text-gray-700">Manage Funding Options</span>
              <ArrowRight size={18} className="text-gray-400" />
            </button>
            <button
              onClick={() => navigate('/admin/inquiries')}
              className="w-full text-left px-4 py-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors flex items-center justify-between"
            >
              <span className="font-medium text-gray-700">View Contact Inquiries</span>
              <ArrowRight size={18} className="text-gray-400" />
            </button>
          </div>
        </Card>

        {/* System Info */}
        <Card className="p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">System Information</h2>
          <div className="space-y-4">
            <div className="flex justify-between items-center py-2 border-b">
              <span className="text-gray-600">Platform</span>
              <span className="font-medium text-gray-900">CSED Management Portal</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b">
              <span className="text-gray-600">Your Role</span>
              <span className="font-medium text-gray-900 capitalize">{user?.role || 'Admin'}</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b">
              <span className="text-gray-600">Email</span>
              <span className="font-medium text-gray-900 text-sm">{user?.email}</span>
            </div>
            <div className="flex justify-between items-center py-2">
              <span className="text-gray-600">Status</span>
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                ● Active
              </span>
            </div>
          </div>
        </Card>
      </div>
    </AdminLayout>
  );
}
